import { Box } from '@unifyapps/ui/components/Box';
import {
  Children,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

type Props = {
  children?: ReactNode;
};

export enum PrintLayoutSlots {
  Header = 'HEADER',
  Body = 'BODY',
  Footer = 'FOOTER',
}

type SlotProps = { name: PrintLayoutSlots; id?: string };

function Slot(props: PropsWithChildren<SlotProps>): null {
  return null;
}

type Dimensions = {
  height: number;
};

// Refer to https://medium.com/@Idan_Co/the-ultimate-print-html-template-with-header-footer-568f415f6d2a for more details
export function PrintLayout({ children }: Props) {
  const exportHeaderRef = useRef<HTMLDivElement>(null);
  const exportFooterRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{
    header?: Dimensions;
    footer?: Dimensions;
  }>({});

  const childrenArr = Children.toArray(children);

  const headerSlot = childrenArr.find(
    (child: ReactElement<SlotProps>) => child.props.name === PrintLayoutSlots.Header,
  ) as ReactElement<{ children: ReactElement<unknown> }> | undefined;

  const bodySlot = childrenArr.find(
    (child: ReactElement<SlotProps>) => child.props.name === PrintLayoutSlots.Body,
  ) as ReactElement<{ children: ReactElement<unknown> }> | undefined;

  const footerSlot = childrenArr.find(
    (child: ReactElement<SlotProps>) => child.props.name === PrintLayoutSlots.Footer,
  ) as ReactElement<{ children: ReactElement<unknown> }> | undefined;

  const isHeaderPresent = Boolean(headerSlot?.props.children);
  const isFooterPresent = Boolean(footerSlot?.props.children);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const isHeader = entry.target === exportHeaderRef.current;

        setDimensions((prev) => ({
          ...prev,
          [isHeader ? 'header' : 'footer']: {
            height: entry.contentRect.height,
          },
        }));
      });
    });

    if (exportHeaderRef.current) {
      resizeObserver.observe(exportHeaderRef.current);
    }
    if (exportFooterRef.current) {
      resizeObserver.observe(exportFooterRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <>
      <table className="h-full w-full" data-testid="export-layout-root">
        <thead>
          <tr>
            <td>
              <Box
                className="hidden print:block"
                data-testid="header-space"
                style={{
                  height: `${dimensions.header?.height ?? 0}px`,
                }}
              >
                &nbsp;
              </Box>
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{bodySlot?.props.children}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td>
              <Box
                className="hidden print:block"
                data-testid="footer-space"
                style={{
                  height: `${dimensions.footer?.height ?? 0}px`,
                }}
              >
                &nbsp;
              </Box>
            </td>
          </tr>
        </tfoot>
      </table>
      <Box
        className="invisible fixed top-0 w-full print:visible"
        ref={exportHeaderRef}
        style={{ pageBreakInside: 'avoid' }}
      >
        {isHeaderPresent ? headerSlot?.props.children : null}
      </Box>
      <Box
        className="invisible fixed bottom-0 w-full print:visible"
        ref={exportFooterRef}
        style={{ pageBreakInside: 'avoid' }}
      >
        {isFooterPresent ? footerSlot?.props.children : null}
      </Box>
    </>
  );
}

PrintLayout.Slot = Slot;
